import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import logger from '../lib/logger';
import {
  Briefcase, Clock, DollarSign, Calendar, Eye, FileText,
  ChevronLeft, ChevronRight, Search, GitCompareArrows, Plus,
  ArrowRight, FileDown, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 12;

export default function QuoteHubPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const fetchEstimates = useCallback(async (p = page, q = search) => {
    setLoading(true);
    try {
      const params = { page: p, limit: PAGE_SIZE };
      if (q) params.search = q;
      const res = await axios.get(`${API_URL}/api/estimates`, { params, withCredentials: true });
      setEstimates(res.data.estimates || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      logger.error('Error fetching estimates:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchEstimates(page, search); }, [page, search, fetchEstimates]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSearch = () => { setPage(1); setSearch(searchInput); };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : prev.length < 2 ? [...prev, id] : prev);
  };

  const getComplexityColor = (label) => {
    switch (label) {
      case 'Simple': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Moderate': return 'bg-sky-50 text-sky-700 border-sky-200';
      case 'Complex': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Very Complex': return 'bg-rose-50 text-rose-700 border-rose-200';
      default: return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">
                Select an estimate to build or view its quote
              </p>
            </div>
            <div className="flex gap-2">
              {estimates.length >= 2 && (
                <Button
                  variant={compareMode ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setCompareMode(!compareMode); setSelected([]); }}
                  className={compareMode ? 'bg-[#0F2040]' : ''}
                  data-testid="bc-compare-toggle"
                >
                  <GitCompareArrows className="w-4 h-4 mr-1" />
                  {compareMode ? 'Cancel Compare' : 'Compare Quotes'}
                </Button>
              )}
              {compareMode && selected.length === 2 && (
                <Button
                  size="sm"
                  className="bg-[#4ABFCF] hover:bg-[#3AAAB9]"
                  onClick={() => navigate(`/compare-quotes?a=${selected[0]}&b=${selected[1]}`)}
                  data-testid="bc-run-compare"
                >
                  <ArrowRight className="w-4 h-4 mr-1" />
                  Compare Selected
                </Button>
              )}
              {!compareMode && isAdmin && (
                <Button
                  size="sm"
                  className="bg-[#4ABFCF] hover:bg-[#3AAAB9]"
                  onClick={() => navigate('/estimator/new')}
                  data-testid="bc-new-estimate"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New Estimate
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="flex gap-2 mb-6" data-testid="bc-search-bar">
          <Input
            placeholder="Search estimates..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="max-w-sm"
            data-testid="bc-search-input"
          />
          <Button variant="outline" size="sm" onClick={handleSearch} data-testid="bc-search-btn">
            <Search className="w-4 h-4" />
          </Button>
          {search && (
            <Button variant="ghost" size="sm" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }} data-testid="bc-search-clear">
              Clear
            </Button>
          )}
          <span className="ml-auto text-sm text-slate-500 self-center" data-testid="bc-total-count">
            {total} estimate{total !== 1 ? 's' : ''}
          </span>
        </div>

        {compareMode && (
          <div className="mb-4 px-3 py-2 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-700" data-testid="bc-compare-hint">
            Select exactly 2 estimates to compare their quotes side-by-side.
            {selected.length > 0 && <span className="font-semibold ml-1">{selected.length}/2 selected</span>}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-4 border-[#4ABFCF] border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-slate-500">Loading estimates...</p>
          </div>
        ) : estimates.length === 0 ? (
          <Card className="max-w-lg mx-auto relative overflow-hidden" data-testid="bc-empty-state">
            <img
              src="/seertech-mark.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none select-none absolute -right-8 -bottom-8 w-48 h-48 opacity-[0.06]"
            />
            <CardContent className="p-12 text-center relative">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
                <Briefcase className="w-8 h-8 text-slate-400" />
              </div>
              <h2 className="text-xl font-semibold text-[#0F2040] mb-2">No Estimates Yet</h2>
              <p className="text-slate-600 mb-6">Create an estimate first, then come back here to build a quote.</p>
              {isAdmin && (
                <Button
                  className="bg-[#4ABFCF] hover:bg-[#3AAAB9]"
                  onClick={() => navigate('/estimator/new')}
                  data-testid="bc-create-first"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Estimate
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {estimates.map((est) => {
              const isSelected = selected.includes(est.id);
              return (
                <Card
                  key={est.id}
                  className={`group transition-all hover:shadow-md cursor-pointer ${
                    compareMode && isSelected ? 'ring-2 ring-[#4ABFCF] shadow-md' : ''
                  } ${compareMode ? 'cursor-pointer' : ''}`}
                  onClick={compareMode ? () => toggleSelect(est.id) : undefined}
                  data-testid={`bc-card-${est.id}`}
                >
                  <CardContent className="p-5">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-[#0F2040] truncate" data-testid={`bc-card-name-${est.id}`}>
                          {est.estimate_name}
                        </h3>
                        <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(est.created_at)}
                        </p>
                      </div>
                      <Badge className={`text-[10px] ml-2 shrink-0 ${getComplexityColor(est.results?.complexity_label)}`}>
                        {est.results?.complexity_label || 'N/A'}
                      </Badge>
                    </div>

                    {/* Metrics */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-slate-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-1 text-slate-400 text-[10px] mb-0.5">
                          <Clock className="w-3 h-3" />
                          Effort
                        </div>
                        <p className="text-sm font-bold text-[#0F2040]">
                          {est.results?.total_effort?.toLocaleString() || 0} <span className="font-normal text-xs">hrs</span>
                        </p>
                      </div>
                      <div className="bg-slate-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-1 text-slate-400 text-[10px] mb-0.5">
                          <DollarSign className="w-3 h-3" />
                          Cost
                        </div>
                        <p className="text-sm font-bold text-[#4ABFCF]">
                          ${est.results?.total_cost?.toLocaleString() || 0}
                        </p>
                      </div>
                    </div>

                    {/* Components preview */}
                    <div className="flex flex-wrap gap-1 mb-4">
                      {est.selected_components?.slice(0, 3).map((c, i) => (
                        <Badge key={i} variant="secondary" className="text-[10px]">
                          {c.component_name}
                        </Badge>
                      ))}
                      {est.selected_components?.length > 3 && (
                        <Badge variant="secondary" className="text-[10px]">
                          +{est.selected_components.length - 3}
                        </Badge>
                      )}
                    </div>

                    {/* Actions */}
                    {!compareMode && (
                      <div className="flex gap-2 pt-3 border-t border-slate-100">
                        <Button
                          size="sm"
                          className="flex-1 bg-[#0F2040] hover:bg-[#0F2040]/90 text-xs"
                          onClick={() => navigate(`/estimator/quote/${est.id}`)}
                          data-testid={`bc-view-case-${est.id}`}
                        >
                          <Briefcase className="w-3.5 h-3.5 mr-1" />
                          View Quote
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/estimator?load=${est.id}`)}
                          title="View estimate"
                          data-testid={`bc-view-estimate-${est.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}

                    {compareMode && (
                      <div className="flex items-center justify-center pt-3 border-t border-slate-100">
                        <span className={`text-xs font-medium ${isSelected ? 'text-[#4ABFCF]' : 'text-slate-400'}`}>
                          {isSelected ? 'Selected' : 'Click to select'}
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8" data-testid="bc-pagination">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              data-testid="bc-pagination-prev"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <span className="text-sm text-slate-600" data-testid="bc-pagination-info">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              data-testid="bc-pagination-next"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
